import React, { type JSX } from 'react';
import { Edit } from '@mui/icons-material';
import CustomIcon from '../../components/CustomIcon';
import { useParams } from 'react-router-dom';
import {
    Container,
    IconButton,
    List,
    Stack,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
    Button,
    Chip,
} from '@mui/material';
import { ChannelValue, InfoListItem } from '@brightlayer-ui/react-components';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { useTheme } from '@mui/material/styles';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { CustomTransComponent } from 'components';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { useTranslation } from 'react-i18next';
import { useGetDevicePropertiesQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { LocationText } from './LocationText';
import { UpdateModal } from './UpdateModal';
import { useDebounce, useRBAC } from 'hooks';
import { useIconLibraryFlag } from '@fiji/common/src/hooks';
import { useTransformData } from 'hooks/useTransformData';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export const Properties = (): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const { isIconLibraryEnabled } = useIconLibraryFlag();
    const [searchKey, setSearchKey] = React.useState<any>(undefined);
    const [expandedCategories, setExpandedCategories] = React.useState<{ [key: string]: boolean }>({});
    const { deviceId } = useParams();

    const updateModalRef = React.useRef<any>(null);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateDevice = hasPermission('edit-devices');
    const { convertDateTime } = useTransformData();
    const [propertiesList, setPropertiesList] = React.useState<any>(undefined);
    const [, debouncedValue] = useDebounce(undefined, 300, searchKey);
    const {
        data: properties,
        isLoading,
        isFetching,
    } = useGetDevicePropertiesQuery({ deviceId: deviceId, debouncedValue });

    React.useEffect(() => {
        setPropertiesList(properties);
        if (properties?.data && Object?.keys(properties?.data)?.length) {
            const requiredCategories: { [key: string]: boolean } = {};
            Object.keys(properties?.data)?.forEach((category: any) => {
                requiredCategories[category] = true;
            });
            setExpandedCategories(requiredCategories);
        }
    }, [properties]);

    const handleAllExpandCollapse = (): void => {
        const isExpanded = Object?.values(expandedCategories)?.every((item: any) => item === true);
        const expandedCategoriesClone = JSON.parse(JSON.stringify(expandedCategories));
        for (const key in expandedCategoriesClone) {
            if (isExpanded) {
                expandedCategoriesClone[key] = false;
            } else {
                expandedCategoriesClone[key] = true;
            }
        }
        setExpandedCategories(expandedCategoriesClone);
    };

    const getRightComponent = (data: any): JSX.Element => {
        const key = data?.key;
        const value = data?.value;

        if (Array?.isArray(value)) {
            return data?.value?.map((item: any) => (
                <Chip key={item?.id ?? item?.groupId} label={item?.name ?? item?.groupName} />
            ));
        }

        if (key === 'batteryInstallDate' || key === 'deviceInstallDate') {
            return (
                <>
                    {value
                        ? convertDateTime({
                              timestamp: value,
                              customFormat: 'LL',
                          })
                        : ''}
                </>
            );
        }

        if (key === 'location') {
            return <LocationText data={data} />;
        }

        if (key === 'description') {
            return <Typography sx={{ wordBreak: 'break-word', maxWidth: '650px' }}>{value ? value : '-'}</Typography>;
        }

        return (
            <ChannelValue
                sx={{
                    '& h6': { wordBreak: 'break-word' },
                    maxWidth: '650px',
                }}
                value={value?.groupName ?? value ?? '-'}
                units={data?.unit ?? '-'}
            />
        );
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderPropertiesAccordions = (item: any): JSX.Element => (
        <Accordion
            key={item}
            expanded={expandedCategories[item] ?? true}
            onChange={(): void => {
                setExpandedCategories((prev: any) => ({ ...prev, [item]: !prev[item] }));
            }}
            defaultExpanded
        >
            <AccordionSummary
                expandIcon={<ExpandMore color="primary" />}
                aria-controls="panel1bh-content"
                id="expand"
                className="border-bottom-1"
            >
                <Typography
                    variant="body2"
                    fontWeight={'600'}
                    fontSize={'14px'}
                    sx={{ color: theme.palette?.primary?.main }}
                >
                    {item ?? '--'}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className="padding-0">
                <List disablePadding>
                    {propertiesList?.data[item]?.map((category: any) => {
                        const { name, iconLibrary } = category?.icon ?? {};
                        return (
                            <InfoListItem
                                sx={category?.key}
                                key={category?.key}
                                className="border-bottom-1"
                                icon={
                                    <CustomIcon
                                        family={
                                            category?.icon?.web?.family ??
                                            (isIconLibraryEnabled ? iconLibrary?.name : undefined)
                                        }
                                        iconName={
                                            category?.icon?.web?.name ?? (isIconLibraryEnabled ? name : undefined)
                                        }
                                    />
                                }
                                title={category?.displayName}
                                rightComponent={
                                    <>
                                        {getRightComponent(category)}
                                        {category?.isEditable && canUpdateDevice && (
                                            <IconButton
                                                id="edit"
                                                onClick={(): void =>
                                                    updateModalRef?.current?.handleModalAction(true, category)
                                                }
                                                className="padding-12"
                                            >
                                                <Edit className="color-gray-500 width-18 height-18" />
                                            </IconButton>
                                        )}
                                    </>
                                }
                            />
                        );
                    })}
                </List>
            </AccordionDetails>
        </Accordion>
    );

    const getAccordionContent = (): any => {
        if (isLoading || isFetching) {
            return (
                <Accordion defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ExpandMore color="primary" />}
                        aria-controls="panel1bh-content"
                        id="expand"
                        className="border-bottom-1"
                    >
                        <Skeleton animation="wave">
                            <Typography
                                variant="body2"
                                fontWeight={'600'}
                                fontSize={'14px'}
                                sx={{ color: theme.palette?.primary?.main }}
                            >
                                <CustomTransComponent translationKey={'COMMON:PRIORITY'} />
                            </Typography>
                        </Skeleton>
                    </AccordionSummary>
                    <AccordionDetails className="padding0">
                        <ListSkeleton total={10} customSize={25} customSx={{ height: '4.5rem' }} />
                    </AccordionDetails>
                </Accordion>
            );
        } else if (Object.keys(propertiesList?.data ?? {})?.length) {
            return Object.keys(propertiesList?.data)?.map((item: any) => renderPropertiesAccordions(item));
        }
        return (
            <CustomEmptyState
                icon
                title={<CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:NO_PROPERTIES'} />}
            />
        );
    };

    return (
        <>
            <Container className="padding-y-36">
                <Stack mb={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Stack direction={'row'} alignItems={'center'} spacing={3}>
                        <InputWithCustomPlaceholder
                            id="searchKey"
                            value={searchKey ?? ''}
                            placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                setSearchKey(e.target.value);
                            }}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Button
                            id="toggleExpand"
                            sx={{
                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.[50],
                                },
                            }}
                            variant="outlined"
                            onClick={handleAllExpandCollapse}
                            startIcon={<UnfoldMore />}
                        >
                            {Object?.values(expandedCategories)?.every((item: any) => item === true) ? (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:COLLAPSE_ALL'} />
                            ) : (
                                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:EXPAND_ALL'} />
                            )}
                        </Button>
                    </Stack>
                </Stack>

                <Stack>{getAccordionContent()}</Stack>
            </Container>
            <UpdateModal ref={updateModalRef} allProperties={properties?.data} />
        </>
    );
};
